import type { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query';

import i18n from '../../../i18n';
import { IAppDispatch, IRootState } from '../../../redux/core-store';
import { setActiveConversationId } from '../../../redux/slices/chat-tab/chat-tab-slice';
import { sendMetrics } from '../../../redux/thunks/metrics-thunk';
import { MixpanelEvent } from '../../../services/mixpanel/types';
import { getToastService } from '../../../services/toast/toast-service';
import { baseApi } from '../../base-api';
import {
    ApiTagTypes,
    IConversation,
    IConversationBase,
    ICreateConversationResponse,
    IDeleteConversation,
    IGetAllConversationParams,
    IGetConversationByIdParams,
    IGetConversationParams,
    IGetConversationsHistoryResponse,
    IGetConversationV2Response,
    IUpdateConversation,
    ResponseStatus,
} from '../../types';

import { findByActionId } from './chat-find-latest';
import { CONVERSATION_FUNC } from './consts';

type _PatchCollection = {
    undo: () => void;
};

export const chatUrl = `${process.env.REACT_APP_CHAT_SERVICE_URL}/conversations`;
export const chatV2Url = `${process.env.REACT_APP_CHAT_SERVICE_URL}/v2/conversations`;

const baseApiWithTags = baseApi.enhanceEndpoints({
    addTagTypes: [ApiTagTypes.CONVERSATION, ApiTagTypes.CONVERSATIONS_HISTORY],
});

export const conversationEndpoints = baseApiWithTags.injectEndpoints({
    endpoints: (builder) => ({
        createConversation: builder.mutation<IConversation, IGetConversationParams>({
            queryFn: async (args, { dispatch }, _extraOptions, baseQuery) => {
                const { customer_project_id, action_id, apps_version } = args;
                let conversation: IConversation | null = null;

                // if action_id is provided (chat opens from answer), try to find the conversation first
                if (action_id) {
                    conversation = await findByActionId({ customer_project_id, action_id }, baseQuery);
                }

                if (!conversation) {
                    const response = await baseQuery({
                        url: chatUrl,
                        method: 'POST',
                        params: { customer_project_id },
                        body: {
                            action_id,
                            apps_version,
                        },
                    });

                    const { error, data, meta } = response as QueryReturnValue<
                        ICreateConversationResponse,
                        FetchBaseQueryError,
                        FetchBaseQueryMeta
                    >;

                    if (error) {
                        const message = i18n.t('error.api.create-conversation', { ns: 'errors' });

                        if (error.status !== ResponseStatus.UNAUTHORIZED) {
                            getToastService().error(message, meta);
                        }

                        return {
                            error,
                        };
                    }

                    conversation = data.conversation;

                    // upsert the data into cache only if it is a new conversation
                    dispatch(
                        conversationEndpoints.util.upsertQueryData(
                            CONVERSATION_FUNC,
                            { customer_project_id, conversation_id: conversation.conversation_id },
                            conversation
                        )
                    );
                }

                dispatch(setActiveConversationId(conversation.conversation_id));

                return {
                    data: conversation,
                };
            },
            invalidatesTags: (result) => {
                return result
                    ? [
                          { type: ApiTagTypes.CONVERSATION, id: result.conversation_id },
                          ApiTagTypes.CONVERSATIONS_HISTORY,
                      ]
                    : [ApiTagTypes.CONVERSATIONS_HISTORY];
            },
        }),
        updateConversation: builder.mutation<IConversation, IUpdateConversation>({
            queryFn: async (args, baseQueryApi, _extraOptions, baseQuery) => {
                const dispatch = baseQueryApi.dispatch as IAppDispatch;
                const { customer_project_id, conversation_id, title, model } = args;

                let oldTitle: null | undefined | string = null;

                let patchCache: _PatchCollection | null = null;
                if (title) {
                    patchCache = dispatch(
                        conversationEndpoints.util.updateQueryData(
                            'getAllConversations',
                            {
                                customer_project_id: customer_project_id,
                            },
                            (draft) => {
                                if (draft?.length > 0) {
                                    const cached = draft.find((c) => c.conversation_id === conversation_id);

                                    if (cached) {
                                        oldTitle = cached.title;
                                        cached.title = title;
                                    }
                                }
                            }
                        )
                    );
                } else if (model) {
                    patchCache = dispatch(
                        conversationEndpoints.util.updateQueryData(
                            CONVERSATION_FUNC,
                            { customer_project_id: customer_project_id, conversation_id: conversation_id },
                            (draft) => {
                                if (draft) {
                                    if (draft.llm_config) {
                                        draft.llm_config.model = model;
                                    }
                                }
                            }
                        )
                    );
                }

                const response = await baseQuery({
                    url: `${chatUrl}/${conversation_id}`,
                    method: 'PATCH',
                    params: { customer_project_id },
                    body: {
                        title,
                        model,
                    },
                });

                const { error, data } = response as QueryReturnValue<ICreateConversationResponse, FetchBaseQueryError>;

                if (error) {
                    patchCache?.undo();
                    const message = i18n.t('error.api.update-conversation', { ns: 'errors' });

                    getToastService().error(message);

                    return {
                        error,
                    };
                }

                if (oldTitle && oldTitle !== title) {
                    dispatch(
                        sendMetrics({
                            event: MixpanelEvent.CHAT_TAB_HISTORY_CONVERSATION_RENAME,
                            meta: {
                                conversation_id,
                                old_title: oldTitle,
                                new_title: title,
                            },
                        })
                    );
                }

                return { data: data.conversation };
            },
            invalidatesTags: [ApiTagTypes.CONVERSATIONS_HISTORY],
        }),
        deleteConversation: builder.mutation<undefined, IDeleteConversation>({
            queryFn: async (args, baseQueryApi, _extraOptions, baseQuery) => {
                const { customer_project_id, conversation_id } = args;

                const dispatch = baseQueryApi.dispatch as IAppDispatch;
                const {
                    chatTab: { activeConversationId },
                } = baseQueryApi.getState() as IRootState;

                let nextConversationId: null | string = null;
                const patchCache = dispatch(
                    conversationEndpoints.util.updateQueryData(
                        'getAllConversations',
                        {
                            customer_project_id: customer_project_id,
                        },
                        (draft) => {
                            if (draft?.length > 0) {
                                const nextDraft = draft.filter((c) => c.conversation_id !== conversation_id);

                                if (nextDraft.length > 0) {
                                    nextConversationId = nextDraft[0].conversation_id;
                                }

                                return nextDraft;
                            }
                        }
                    )
                );
                if (conversation_id === activeConversationId) {
                    dispatch(setActiveConversationId(nextConversationId));
                }

                const response = await baseQuery({
                    url: `${chatUrl}/${conversation_id}`,
                    method: 'DELETE',
                    params: { customer_project_id },
                });

                const { error } = response as QueryReturnValue<ICreateConversationResponse, FetchBaseQueryError>;

                if (error) {
                    patchCache.undo();
                    dispatch(setActiveConversationId(activeConversationId));

                    const message = i18n.t('error.api.delete-conversation', { ns: 'errors' });

                    getToastService().error(message);

                    return {
                        error,
                    };
                }

                dispatch(
                    sendMetrics({
                        event: MixpanelEvent.CHAT_TAB_HISTORY_CONVERSATION_DELETE,
                        meta: {
                            conversation_id,
                        },
                    })
                );

                return { data: undefined };
            },
            invalidatesTags: [ApiTagTypes.CONVERSATIONS_HISTORY],
        }),
        [CONVERSATION_FUNC]: builder.query<IConversationBase, IGetConversationByIdParams>({
            queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
                const { customer_project_id, conversation_id } = args;
                const response = await baseQuery({
                    url: `${chatV2Url}/${conversation_id}`,
                    params: { customer_project_id },
                    method: 'GET',
                });

                const { error, data } = response as QueryReturnValue<
                    IGetConversationV2Response,
                    FetchBaseQueryError,
                    FetchBaseQueryMeta
                >;

                if (error) {
                    if (error.status !== ResponseStatus.NOT_FOUND) {
                        const message = i18n.t('error.api.get-conversation', { ns: 'errors' });
                        getToastService().error(message);
                    }

                    return { error };
                }
                return { data: data.conversation };
            },
            providesTags: (result) => {
                return result
                    ? [{ type: ApiTagTypes.CONVERSATION, id: result.conversation_id }, ApiTagTypes.CONVERSATION]
                    : [ApiTagTypes.CONVERSATION];
            },
        }),
        getAllConversations: builder.query<IConversationBase[], IGetAllConversationParams>({
            queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
                const { customer_project_id } = args;
                const response = await baseQuery({
                    url: chatUrl,
                    params: { customer_project_id },
                    method: 'GET',
                });

                const { error, data } = response as QueryReturnValue<
                    IGetConversationsHistoryResponse,
                    FetchBaseQueryError,
                    FetchBaseQueryMeta
                >;

                if (error) {
                    if (error.status !== ResponseStatus.NOT_FOUND) {
                        const message = i18n.t('error.api.get-conversation', { ns: 'errors' });
                        getToastService().error(message);
                    }

                    return { error };
                }

                return { data: data.conversations };
            },
            providesTags: [ApiTagTypes.CONVERSATIONS_HISTORY],
        }),
    }),
});

export const {
    useCreateConversationMutation,
    useGetAllConversationsQuery,
    useGetConversationByIdQuery,
    useUpdateConversationMutation,
    useDeleteConversationMutation,
} = conversationEndpoints;
